import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Users, TrendingUp, Star, Eye, Code2, Play, Target, Zap, Shield, Filter } from 'lucide-react';
import Button from '../../../components/ui/Button';

const CompletedFeatures = () => {
  const [timeFilter, setTimeFilter] = useState('all'); // all, recent, quarter
  const [categoryFilter, setCategoryFilter] = useState('all');

  const completedFeatures = [
    {
      id: 1,
      title: 'Real-time Code Execution',
      description: 'Live browser execution environment with instant feedback and screenshot capture for debugging.',
      category: 'Core Platform',
      completionDate: '2024-12-15',
      version: 'v2.1.0',
      impact: {
        userSatisfaction: 4.8,
        usageIncrease: 45,
        communityVotes: 523
      },
      icon: <Play className="w-5 h-5" />,
      releaseNotes: [
        'Docker-based secure execution environment',
        'Real-time screenshot capture during test execution',
        'Enhanced error reporting with stack traces',
        'Support for multiple browser engines'
      ],
      metrics: {
        missionsCompleted: 12500,
        averageExecutionTime: '2.3s',
        bugReports: 3
      }
    },
    {
      id: 2,
      title: 'Mission Progress Tracking',
      description: 'Gamified learning path with achievements, XP system, and visual progress indicators.',
      category: 'User Experience',
      completionDate: '2024-11-28',
      version: 'v2.0.5',
      impact: {
        userSatisfaction: 4.7,
        usageIncrease: 38,
        communityVotes: 467
      },
      icon: <Target className="w-5 h-5" />,
      releaseNotes: [
        'Visual mission map with unlockable content',
        'Achievement system with badges and rewards',
        'Progress analytics and personal statistics',
        'Social sharing of completed missions'
      ],
      metrics: {
        achievementsUnlocked: 8750,
        averageCompletionRate: '78%',
        dailyActiveUsers: 1250
      }
    },
    {
      id: 3,
      title: 'Advanced Code Editor',
      description: 'Monaco Editor integration with Selenium-specific autocomplete, syntax highlighting, and error detection.',
      category: 'Developer Tools',
      completionDate: '2024-11-10',
      version: 'v1.9.2',
      impact: {
        userSatisfaction: 4.9,
        usageIncrease: 52,
        communityVotes: 612
      },
      icon: <Code2 className="w-5 h-5" />,
      releaseNotes: [
        'VS Code-like editing experience',
        'Intelligent Selenium method suggestions',
        'Real-time syntax error highlighting',
        'Code formatting and beautification'
      ],
      metrics: {
        linesOfCode: 245000,
        autocompleteUsage: '89%',
        errorReduction: '34%'
      }
    },
    {
      id: 4,
      title: 'User Authentication & Profiles',
      description: 'Secure login system with personalized profiles, progress saving, and social features.',
      category: 'Security',
      completionDate: '2024-10-22',
      version: 'v1.8.0',
      impact: {
        userSatisfaction: 4.6,
        usageIncrease: 28,
        communityVotes: 389
      },
      icon: <Shield className="w-5 h-5" />,
      releaseNotes: [
        'OAuth integration with Google and GitHub',
        'Secure password hashing and storage',
        'Profile customization and avatars',
        'Progress synchronization across devices'
      ],
      metrics: {
        registeredUsers: 2400,
        loginSuccessRate: '99.2%',
        profileCompletions: '67%'
      }
    },
    {
      id: 5,
      title: 'Mission Result Analytics',
      description: 'Detailed execution reports with performance metrics, code analysis, and improvement suggestions.',
      category: 'Analytics',
      completionDate: '2024-10-08',
      version: 'v1.7.5',
      impact: {
        userSatisfaction: 4.5,
        usageIncrease: 31,
        communityVotes: 334
      },
      icon: <TrendingUp className="w-5 h-5" />,
      releaseNotes: [
        'Comprehensive execution time analysis',
        'Code quality scoring and suggestions',
        'Performance comparison with community',
        'Downloadable PDF reports'
      ],
      metrics: {
        reportsGenerated: 15600,
        averageScore: 82.5,
        improvementRate: '43%'
      }
    },
    {
      id: 6,
      title: 'Enhanced Error Handling',
      description: 'Improved error messages with context-aware suggestions and debugging assistance.',
      category: 'Developer Experience',
      completionDate: '2024-09-25',
      version: 'v1.6.8',
      impact: {
        userSatisfaction: 4.4,
        usageIncrease: 22,
        communityVotes: 298
      },
      icon: <Zap className="w-5 h-5" />,
      releaseNotes: [
        'Context-aware error explanations',
        'Suggested fixes for common issues',
        'Interactive debugging tools',
        'Error categorization and filtering'
      ],
      metrics: {
        errorResolutionRate: '76%',
        supportTicketsReduced: '45%',
        userHelpRequests: 120
      }
    }
  ];

  const categories = ['all', 'Core Platform', 'User Experience', 'Developer Tools', 'Security', 'Analytics', 'Developer Experience'];

  const filteredFeatures = completedFeatures?.filter(feature => {
    const matchesCategory = categoryFilter === 'all' || feature?.category === categoryFilter;
    
    const matchesTime = (() => {
      if (timeFilter === 'all') return true;
      const completionDate = new Date(feature.completionDate);
      const now = new Date();
      
      if (timeFilter === 'recent') {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return completionDate >= thirtyDaysAgo;
      }
      
      if (timeFilter === 'quarter') {
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        return completionDate >= quarterStart;
      }
      
      return true;
    })();
    
    return matchesCategory && matchesTime;
  });

  const getCategoryColor = (category) => {
    const colors = {
      'Core Platform': 'bg-blue-500/10 text-blue-500',
      'User Experience': 'bg-green-500/10 text-green-500',
      'Developer Tools': 'bg-purple-500/10 text-purple-500',
      'Security': 'bg-red-500/10 text-red-500',
      'Analytics': 'bg-orange-500/10 text-orange-500',
      'Developer Experience': 'bg-pink-500/10 text-pink-500'
    };
    return colors?.[category] || 'bg-muted/10 text-muted-foreground';
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold">Recently Completed Features</h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
          Discover the latest improvements and features we've delivered to enhance your learning experience.
        </p>
      </div>
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-center">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Time:</span>
          <select 
            value={timeFilter} 
            onChange={(e) => setTimeFilter(e?.target?.value)}
            className="bg-background border border-border rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Time</option>
            <option value="recent">Last 30 Days</option>
            <option value="quarter">This Quarter</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Category:</span>
          <select 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e?.target?.value)}
            className="bg-background border border-border rounded-md px-3 py-2 text-sm"
          >
            {categories?.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Features List */}
      <div className="grid gap-8">
        {filteredFeatures?.map((feature, index) => (
          <motion.div
            key={feature?.id}
            className="bg-card border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Feature Info */}
              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center text-white">
                      {feature?.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-1">{feature?.title}</h3>
                      <div className="flex items-center space-x-3">
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(feature?.category)}`}>
                          {feature?.category}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {feature?.version}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 text-green-500">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Completed</span>
                  </div>
                </div>

                <p className="text-muted-foreground leading-relaxed">
                  {feature?.description}
                </p>

                {/* Release Notes */}
                <div>
                  <h4 className="font-medium mb-2">Key Improvements:</h4>
                  <ul className="space-y-1">
                    {feature?.releaseNotes?.map((note, i) => (
                      <li key={i} className="flex items-start space-x-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>Released {new Date(feature.completionDate)?.toLocaleDateString()}</span>
                </div>
              </div>

              {/* Metrics & Impact */}
              <div className="space-y-4">
                {/* Impact Metrics */}
                <div className="bg-muted/5 rounded-lg p-4">
                  <h4 className="font-medium mb-3">Impact Metrics</h4>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm">Satisfaction</span>
                      </div>
                      <span className="font-semibold text-yellow-500">
                        {feature?.impact?.userSatisfaction}/5
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <span className="text-sm">Usage Increase</span>
                      </div>
                      <span className="font-semibold text-green-500">
                        +{feature?.impact?.usageIncrease}%
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="text-sm">Community Votes</span>
                      </div>
                      <span className="font-semibold text-blue-500">
                        {feature?.impact?.communityVotes}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Usage Statistics */}
                <div className="bg-muted/5 rounded-lg p-4">
                  <h4 className="font-medium mb-3">Usage Statistics</h4>
                  
                  <div className="space-y-2 text-sm">
                    {Object.entries(feature?.metrics)?.map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-muted-foreground capitalize">
                          {key?.replace(/([A-Z])/g, ' $1')?.trim()}:
                        </span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button variant="outline" size="sm" className="w-full">
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {filteredFeatures?.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-muted/20 rounded-full flex items-center justify-center mb-4">
            <Filter className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No features found</h3>
          <p className="text-muted-foreground">Try adjusting your filters to see more results.</p>
        </div>
      )}
      {/* Summary Stats */}
      <div className="bg-muted/5 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 text-center">Delivery Impact</h3>
        <div className="grid md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-2xl font-bold text-green-500 mb-1">
              {completedFeatures?.length}
            </div>
            <div className="text-sm text-muted-foreground">Features Delivered</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-blue-500 mb-1">
              {(completedFeatures?.reduce((sum, f) => sum + f?.impact?.userSatisfaction, 0) / completedFeatures?.length)?.toFixed(1)}
            </div>
            <div className="text-sm text-muted-foreground">Avg Satisfaction</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-purple-500 mb-1">
              {completedFeatures?.reduce((sum, f) => sum + f?.impact?.usageIncrease, 0)}%
            </div>
            <div className="text-sm text-muted-foreground">Total Usage Growth</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-orange-500 mb-1">
              {completedFeatures?.reduce((sum, f) => sum + f?.impact?.communityVotes, 0)}
            </div>
            <div className="text-sm text-muted-foreground">Community Votes</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletedFeatures;